import { responsive, useTheme } from '@wci-tech/core-ui';
import { css } from '@emotion/react';
import { useMemo } from 'react';

import { getContainerStyles } from '@/utils';

export const useStyles = () => {
  const { theme } = useTheme();

  return useMemo(
    () => ({
      wrapper: css`
        width: 100%;
        overflow: hidden;
      `,

      container: css`
        ${getContainerStyles(theme)};

        ${responsive.create({
          paddingTop: {
            default: theme.spacing.get(64),
            md: theme.spacing.get(92),
            lg: theme.spacing.get(132),
          },
          paddingBottom: {
            default: theme.spacing.get(64),
            md: theme.spacing.get(92),
            lg: theme.spacing.get(132),
          },
        })}
      `,

      sectionTitle: css`
        position: relative;
        text-align: center;
        color: var(--text-color);

        ${responsive.create({
          fontSize: {
            default: '20px',
            md: '30px',
            lg: '36px',
          },
        })}

        &::after {
          content: '';
          display: block;
          margin: 20px auto 0;
          background: linear-gradient(88.68deg, #4054d1 8.83%, #17ceca 115.83%);

          ${responsive.create({
            width: {
              default: '64px',
              md: '142px',
              lg: '200px',
            },
            height: {
              default: '4px',
              md: '8px',
              lg: '12px',
            },
            borderRadius: {
              default: '16px',
              md: '50px',
              lg: '36px',
            },
            marginTop: {
              default: '12px',
              md: '20px',
              lg: '20px',
            },
            marginBottom: {
              default: '36px',
              md: '84px',
              lg: '120px',
            },
          })}
        }
      `,

      cardContainer: css`
        scroll-margin-top: 150px;
        background: transparent;
        border: none;
        box-shadow: none;

        ${responsive.create({
          marginBottom: {
            default: '64px',
            md: '80px',
            lg: '100px',
          },
          paddingLeft: {
            default: '0px',
            xxl: '100px',
          },
          paddingRight: {
            default: '0px',
            xxl: '100px',
          },
        })}

        &:last-child {
          margin-bottom: 0;
        }
      `,

      /*
       * Mobile only.
       *
       * Flex + align-items:flex-start is important:
       * it keeps Badge at its natural content width.
       */
      mobileHeader: css`
        width: 100%;
        flex-direction: column;
        align-items: flex-start;

        ${responsive.create({
          display: {
            default: 'flex',
            md: 'none',
          },
        })}
      `,

      /*
       * Tablet/Desktop.
       *
       * Same flex behaviour as the original contentCol,
       * so the badge does NOT stretch.
       */
      desktopHeader: css`
        width: 100%;
        flex-direction: column;
        align-items: flex-start;

        ${responsive.create({
          display: {
            default: 'none',
            md: 'flex',
          },
        })}
      `,

      contentItem: (isContentLeft: boolean) => css`
        box-sizing: border-box;
        width: 100%;
        min-width: 0;

        ${responsive.create({
          width: {
            default: '100%',
            md: '50%',
          },
          maxWidth: {
            default: '100%',
            md: '50%',
          },
          flex: {
            default: '0 0 100%',
            md: '0 0 50%',
          },
          flexBasis: {
            default: '100%',
            md: '50%',
          },
          order: {
            default: 2,
            md: isContentLeft ? 1 : 2,
          },
          paddingRight: {
            default: '0px',
            md: isContentLeft ? '20px' : '0px',
            lg: isContentLeft ? '28px' : '0px',
            xl: isContentLeft ? '66px' : '0px',
          },
          paddingLeft: {
            default: '0px',
            md: isContentLeft ? '0px' : '20px',
            lg: isContentLeft ? '0px' : '28px',
            xl: isContentLeft ? '0px' : '66px',
          },
        })}
      `,

      imageItem: (isContentLeft: boolean) => css`
        box-sizing: border-box;
        width: 100%;
        min-width: 0;

        ${responsive.create({
          width: {
            default: '100%',
            md: '50%',
          },
          maxWidth: {
            default: '100%',
            md: '50%',
          },
          flex: {
            default: '0 0 100%',
            md: '0 0 50%',
          },
          flexBasis: {
            default: '100%',
            md: '50%',
          },
          order: {
            default: 1,
            md: isContentLeft ? 2 : 1,
          },
          paddingLeft: {
            default: '0px',
            md: isContentLeft ? '20px' : '0px',
            lg: isContentLeft ? '28px' : '0px',
            xl: isContentLeft ? '66px' : '0px',
          },
          paddingRight: {
            default: '0px',
            md: isContentLeft ? '0px' : '20px',
            lg: isContentLeft ? '0px' : '28px',
            xl: isContentLeft ? '0px' : '66px',
          },
        })}
      `,

      contentCol: css`
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        width: 100%;
        height: 100%;
        min-width: 0;
        text-align: left;
      `,

      imageCol: css`
        display: flex;
        justify-content: flex-start;
        align-items: flex-start;
        width: 100%;
        min-width: 0;
      `,

      image: css`
        display: block;
        width: 100%;
        max-width: 100%;
        height: auto;
        margin: 0;

        ${responsive.create({
          borderRadius: {
            default: '12px',
            md: '14px',
            lg: '20px',
          },
        })}
      `,

      /*
       * Original badge styling unchanged.
       */
      badge: (bg: string, color: string) => css`
        background: ${bg};
        border: ${bg};
        color: ${color};
        border-radius: 10px;

        ${responsive.create({
          paddingTop: {
            default: '4px',
            md: '16px',
            lg: '24px',
          },
          paddingRight: {
            default: '8px',
            md: '16px',
            lg: '24px',
          },
          paddingBottom: {
            default: '4px',
            md: '16px',
            lg: '24px',
          },
          paddingLeft: {
            default: '8px',
            md: '16px',
            lg: '24px',
          },
        })}
      `,

      /*
       * Explicitly content-sized so neither the mobile nor
       * desktop wrapper can stretch the Badge.
       */
      badgeWrapper: css`
        display: inline-flex;
        width: fit-content;
        max-width: max-content;
        align-self: flex-start;

        ${responsive.create({
          marginBottom: {
            default: '12px',
            md: '16px',
          },
        })}
      `,

      title: css`
        margin: 0;
        color: var(--text-color);

        ${responsive.create({
          marginBottom: {
            default: '20px',
            md: '10px',
            lg: '16px',
          },
          fontSize: {
            default: '20px',
            md: '34px',
            lg: '36px',
            xl: '48px',
          },
        })}
      `,

      description: css`
        width: 100%;
        color: var(--desc-text-color);

        ${responsive.create({
          maxWidth: {
            default: '100%',
            md: '90%',
            lg: '85%',
            xl: '80%',
          },
          marginBottom: {
            default: '20px',
            md: '16px',
            lg: '24px',
          },
          fontSize: {
            default: '14px',
            md: '18px',
            lg: '16px',
            xl: '20px',
          },
        })}
      `,

      highlightText: css`
        color: var(--brand-title);
        font-weight: 500;

        ${responsive.create({
          marginBottom: {
            default: '20px',
            md: '24px',
            lg: '36px',
          },
          fontSize: {
            default: '16px',
            md: '24px',
            lg: '28px',
            xl: '36px',
          },
        })}
      `,

      buttonContainer: css`
        margin-top: 0;

        ${responsive.create({
          width: {
            default: '100%',
            md: 'auto',
          },
        })}
      `,

      btnIcon: css`
        svg {
          stroke-width: 0.6;
        }
      `,
    }),
    [theme],
  );
};
